import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, ExternalLink, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { formatDistanceToNow } from 'date-fns';
import { useAdminNotifications } from '@/contexts/AdminNotificationsContext';

const TYPE_ICON: Record<string, string> = {
  system: '⚙️', alert: '🚨', chat: '💬', mention: '📢', approval: '✅',
  task: '📋', onboarding: '🧑‍💼', company: '🏢', report: '📊',
  compliance: '🔴', message: '💬', ticket: '🎫', doc_request: '📄',
  onboarding_alert: '📋', sweep: '🔄', info: 'ℹ️',
};

const SCOPE_LABEL: Record<string, { label: string; color: string }> = {
  personal: { label: 'You', color: 'bg-primary/10 text-primary' },
  company: { label: 'Company', color: 'bg-emerald-500/10 text-emerald-600' },
  department: { label: 'Dept', color: 'bg-amber-500/10 text-amber-600' },
};

export function AdminNotificationBell() {
  const navigate = useNavigate();
  const { notifications, unreadCount, loading, markRead, markAllRead, isRead } = useAdminNotifications();
  const [open, setOpen] = useState(false);

  const handleClick = async (n: typeof notifications[0]) => {
    await markRead(n);
    if (n.link) { navigate(n.link); setOpen(false); }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative h-8 w-8">
          <Bell className="h-4 w-4 text-muted-foreground" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 bg-destructive text-destructive-foreground text-[9px] font-bold rounded-full min-w-[16px] h-4 flex items-center justify-center px-1 leading-none">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[380px] p-0 shadow-xl flex flex-col max-h-[calc(100vh-80px)]">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border/50 shrink-0">
          <div className="flex items-center gap-2">
            <Bell className="h-4 w-4 text-muted-foreground" />
            <h4 className="text-sm font-semibold">Notifications</h4>
            {unreadCount > 0 && (
              <Badge className="text-[10px] h-4 px-1.5">{unreadCount} new</Badge>
            )}
          </div>
          <div className="flex items-center gap-1">
            {unreadCount > 0 && (
              <Button variant="ghost" size="sm" className="h-6 text-[10px] gap-1" onClick={markAllRead}>
                <CheckCheck className="w-3 h-3" /> Mark all read
              </Button>
            )}
            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setOpen(false)}>
              <X className="h-3 w-3" />
            </Button>
          </div>
        </div>

        {/* Scope legend */}
        <div className="flex gap-2 px-4 py-2 border-b border-border/30 bg-muted/20 shrink-0">
          {Object.entries(SCOPE_LABEL).map(([scope, { label, color }]) => (
            <span key={scope} className={cn('text-[9px] font-semibold px-1.5 py-0.5 rounded-full', color)}>
              {label}
            </span>
          ))}
          <span className="text-[9px] text-muted-foreground ml-auto">Shared team feed</span>
        </div>

        <ScrollArea className="flex-1 overflow-y-auto min-h-0">
          {loading ? (
            <div className="py-10 text-center text-xs text-muted-foreground">Loading…</div>
          ) : notifications.length === 0 ? (
            <div className="py-12 text-center">
              <Bell className="h-10 w-10 mx-auto mb-2 opacity-10" />
              <p className="text-xs text-muted-foreground">All caught up!</p>
            </div>
          ) : (
            <div className="divide-y divide-border/30">
              {notifications.map(n => {
                const read = isRead(n);
                const scope = n.scope || 'personal';
                const scopeMeta = SCOPE_LABEL[scope] || SCOPE_LABEL.personal;
                return (
                  <button
                    key={n.id}
                    onClick={() => handleClick(n)}
                    className={cn(
                      'w-full text-left px-4 py-3 hover:bg-muted/40 transition-colors flex gap-3',
                      !read && 'bg-primary/5 border-l-2 border-l-primary'
                    )}
                  >
                    <span className="text-xl shrink-0 mt-0.5">{TYPE_ICON[n.type] || '🔔'}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <span className={cn('text-[9px] font-bold px-1.5 py-0.5 rounded-full', scopeMeta.color)}>
                          {scopeMeta.label}
                        </span>
                        <span className="text-[10px] text-muted-foreground">
                          {formatDistanceToNow(new Date(n.created_at), { addSuffix: true })}
                        </span>
                      </div>
                      <p className={cn('text-xs leading-snug', !read && 'font-semibold')}>{n.title}</p>
                      {n.body && <p className="text-[10px] text-muted-foreground mt-0.5 line-clamp-2">{n.body}</p>}
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      {!read && <div className="w-2 h-2 rounded-full bg-primary mt-1" />}
                      {n.link && <ExternalLink className="w-3 h-3 text-muted-foreground" />}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </ScrollArea>

        <div className="px-4 py-2 border-t border-border/30 bg-muted/10 shrink-0">
          <Button variant="ghost" size="sm" className="w-full text-xs h-7" onClick={() => { navigate('/admin/inbox'); setOpen(false); }}>
            View all in Inbox →
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
